import { Map } from 'immutable';

import { getProfileSections, setAll } from '@/utils/profiles/sections';
import { shapeAiOperationsForAPI } from '@/utils/profiles/ai';
import { shapeTemplateForAPI } from '@/utils/profiles/templates';
import { shapeSectionsForAPI } from '@/utils/profiles/sections';
import { getSize } from '@/utils/iterable/getSize';

import { PROFILE_TYPE } from '@/constants/profiles';
import { CHANNEL_ID } from '@/constants/channels';
import { FEATURE } from '@/constants/features';
import { EMPTY } from '@/constants';
import { NEW } from '@/constants/product';

import actions from '@/actions';

export function shapeProfilesForApp({ profiles: source, request, response }) {
  const channel = source.get('channel');
  const { type } = request;

  function reduceProfiles(result, data) {
    const { connected_listings: connectedListings, id, name, sections } = data;

    let profile = Map({
      id,
      listings: Map({ total: connectedListings || 0 }),
      name,
      sections: setAll(getProfileSections({ channel, type, value: sections })),
      type,
    });

    function updateLoading(loading) {
      return loading.add(id);
    }

    switch (type) {
      case PROFILE_TYPE.AI: {
        if (source.hasIn(['byId', id, 'ai'])) {
          profile = profile.set('ai', source.getIn(['byId', id, 'ai']));
        } else {
          result.profiles = result.profiles.update('loading', updateLoading);
          result.aiProfileIds = result.aiProfileIds.push(id);
        }

        break;
      }

      case PROFILE_TYPE.STANDARD: {
        const { product_template_id: templateId, template_mode: templateMode } = data;
        profile = profile
          .set('templateId', templateId)
          .set('templateMode', templateMode);

        if (source.hasIn(['byId', id, 'template'])) {
          profile = profile.set('template', source.getIn(['byId', id, 'template']));
        } else {
          result.profiles = result.profiles.update('loading', updateLoading);
          result.templates = result.templates
            .update('profileIds', function updateProfileIds(profileIds = EMPTY.LIST) {
              return profileIds.push(id);
            })
            .update('templateIds', function updateTemplateIds(templateIds = EMPTY.LIST) {
              return templateIds.push(templateId);
            })
            .update('templateToProfileMap', function updateTemplateToProfileMap(templateToProfileMap = EMPTY.MAP) {
              return templateToProfileMap.set(templateId, id);
            });
        }

        break;
      }

      default: {
        break;
      }
    }

    function updateById(byId) {
      return byId.set(id, profile);
    }

    function updateIds(ids) {
      return ids.push(id);
    }

    result.profiles = result.profiles.update('byId', updateById).update('ids', updateIds);

    if (source.getIn(['editing', 'id']) === id) {
      result.profiles = result.profiles.set('editing', profile);
    }

    return result;
  }

  function updatePagination(pagination) {
    const total = response.count;
    const page = pagination.get('page');
    const pageSize = pagination.get('pageSize');
    const offset = page * pageSize;

    return pagination
      .set('end', Math.min(total, offset + pageSize))
      .set('start', offset)
      .set('total', total);
  }

  function updateEditing(editing = EMPTY.MAP) {
    return editing.get('id') === NEW
      ? editing
      : EMPTY.MAP;
  }

  const actionsToDispatch = [];
  const { aiProfileIds, profiles, templates } = response.data.reduce(
    reduceProfiles,
    {
      aiProfileIds: EMPTY.LIST,
      profiles: source
        .set('byId', EMPTY.MAP)
        .set('ids', EMPTY.LIST)
        .set('loading', EMPTY.SET)
        .set('request', Map(request))
        .set('total', response.count)
        .set('section', source.getIn(['filters', 'selected']))
        .update('editing', updateEditing)
        .update('pagination', updatePagination),
      templates: EMPTY.MAP,
    }
  );

  if (getSize(aiProfileIds) > 0) {
    actionsToDispatch.push(actions.profiles.getAiOperations({ profileIds: aiProfileIds }));
  }

  if (getSize(templates) > 0) {
    actionsToDispatch.push(actions.profiles.getTemplates(templates));
  }

  return { actions: actionsToDispatch, profiles };
}

export function shapeProfileForAPI({ profile, shop, shopData, userFeatures }) {
  const channel = shop.get('channel');
  const payload = { name: profile.get('name'), sections: shapeSectionsForAPI({ channel, profile }) };

  switch (profile.get('type')) {
    case PROFILE_TYPE.AI: {
      payload.channelId = CHANNEL_ID[channel];
      payload.operations = shapeAiOperationsForAPI({ state: profile.get('ai') });
      payload.userDb = shop.get('db');
      break;
    }

    case PROFILE_TYPE.STANDARD: {
      payload.template = shapeTemplateForAPI({ shop, shopData, template: profile.get('template'), userFeatures });
      payload.template_mode = profile.get('templateMode');
      break;
    }

    default: {
      break;
    }
  }

  return payload;
}

export function isComplete(profile) {
  switch (profile.get('type')) {
    case PROFILE_TYPE.AI: {
      return true;
    }

    case PROFILE_TYPE.STANDARD: {
      return profile.getIn(['template', 'isComplete']);
    }

    default: {
      return false;
    }
  }
}

export function getShopProfiles({ shopData, templates, userFeatures }) {
  let shopProfiles = shopData.get('profiles', EMPTY.MAP);

  function reduceProfileIds(result, profileId) {
    const profile = shopData.getIn(['profiles', PROFILE_TYPE.STANDARD, 'byId', profileId]);

    if (profile.get('templateMode')) return result;

    function updateById(byId = EMPTY.MAP) {
      return byId.set(profileId, profile);
    }

    function updateIds(ids = EMPTY.LIST) {
      return ids.push(profileId);
    }

    return result
      .update('byId', updateById)
      .update('ids', updateIds);
  }

  if (!userFeatures.get(FEATURE.AI_PROFILES)) {
    shopProfiles = shopProfiles.delete(PROFILE_TYPE.AI);
  }

  if (!templates) {
    shopProfiles = shopProfiles.set(PROFILE_TYPE.STANDARD,
      shopData.getIn(['profiles', PROFILE_TYPE.STANDARD, 'ids'], EMPTY.LIST).reduce(reduceProfileIds, EMPTY.MAP)
    );

  }

  return shopProfiles;
}

